/**
 * AI Insight conversations kept in the browser (localStorage), one per tenant, user and agent,
 * until sign-out and for at most 7 days without an update. A conversation carries its dashboard
 * canvas (widgets) and, once saved, which DOC dashboard it was saved as.
 *
 * Every read and write is wrapped: without storage (private mode, quota, blocked) the chat still
 * works for the page's lifetime. Its only runtime import is the small, pure widgets module, so the
 * sign-out paths can call clearAiInsightConversations cheaply.
 */
import type { ConfirmOption, InsightAgentRef, InsightToolCall } from './insightAgentConfig';
import {
  capRows,
  type CanvasWidget,
  type DashboardChange,
  type DashboardFilters,
  type DashboardSummary,
  type DashboardVisibility,
  type SuggestedFilter,
} from './dashboard/widgets';

export const STORAGE_PREFIX = 'ai-insight:';
const VERSION = 'v1';
export const MAX_STORED_TURNS = 50;
/** Only the newest turns keep "What the agent ran"; older ones keep the answer alone. */
export const TOOL_CALL_TURNS = 10;
export const MAX_TOOL_RESULT = 1_000;
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** About 2 MB of JSON per conversation; past that the oldest widgets lose their data first. */
export const MAX_STORED_CHARS = 2_000_000;

/** A tool call with a key fixed when its answer arrives (the list never changes after that). */
export type KeyedToolCall = InsightToolCall & { uid: string };

export interface ChatTurn {
  id: number;
  /** The agent the question went to ('' for Insight Agent, the default); history never mixes agents. */
  scope: string;
  question: string;
  /** interrupted: the page closed while the answer was running. */
  status: 'running' | 'done' | 'error' | 'interrupted';
  answer?: string;
  toolCalls?: KeyedToolCall[];
  agent?: InsightAgentRef;
  warnings?: string[];
  error?: string;
  seconds?: number;
  /** How many widgets this answer added to the canvas. */
  widgetCount?: number;
  /** What this request did to the dashboard (dashboard mode). */
  change?: DashboardChange;
  /** A clarifying reply's choices (AI-9689): picking one fills the chat box, to edit before sending. */
  options?: string[];
  /** Asked before replacing an open dashboard: the choices, offered on the latest turn only. */
  confirm?: ConfirmOption[];
}

/** The DOC dashboard this conversation's canvas was saved as: saving again PATCHes it. */
export interface SavedDashboardRef {
  configId: string;
  configurationId: string;
  title: string;
  description: string;
  visibility: DashboardVisibility;
}

/** A conversation: its turns (oldest first), its canvas, and the dashboard it was saved as. */
export interface ChatSession {
  turns: ChatTurn[];
  widgets: CanvasWidget[];
  dashboard?: SavedDashboardRef;
  /** The dashboard's title and its latest summary, from the agent (dashboard mode). */
  title?: string;
  summary?: DashboardSummary;
  /**
   * The filter bar: the dashboard's filters (null for none), from the agent's last update or the bar.
   * Undefined until either sets them; requests then leave filters out and the service uses its own.
   */
  filters?: DashboardFilters | null;
  /** Filters the bar offers, gathered from the agent's updates. */
  suggestedFilters?: SuggestedFilter[];
}

/** The session's optional fields that are set, so stored JSON stays small. */
function extras(session: Partial<ChatSession>): Partial<ChatSession> {
  return {
    ...(session.dashboard?.configId ? { dashboard: session.dashboard } : {}),
    ...(typeof session.title === 'string' && session.title ? { title: session.title } : {}),
    ...(session.summary?.text && session.summary.at ? { summary: session.summary } : {}),
    ...(session.filters !== undefined ? { filters: session.filters } : {}),
    ...(session.suggestedFilters?.length ? { suggestedFilters: session.suggestedFilters } : {}),
  };
}

interface StoredConversation extends ChatSession {
  updatedAt: number;
}

export const EMPTY_SESSION: ChatSession = { turns: [], widgets: [] };

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null; // e.g. blocked by the browser's privacy settings
  }
}

const DEFAULT_SUFFIX = 'default';
/** Before the default agent was "Insight Agent", its conversation was keyed `builtin`. */
const LEGACY_DEFAULT_SUFFIX = 'builtin';

/**
 * `ai-insight:v1:<tenant>:<sub>:<agentId|default>`, or null when the user is not known yet. The
 * default agent (no agent id) is always `default`, whether or not it has been customised, so its
 * conversation never splits.
 */
export function conversationKey(tenant: string, sub: string, agentId: string): string | null {
  if (!tenant || !sub) return null;
  return `${STORAGE_PREFIX}${VERSION}:${tenant}:${sub}:${agentId || DEFAULT_SUFFIX}`;
}

/** Moves a conversation stored under the old `builtin` key to `default`, once. */
function migrateLegacyDefault(key: string): void {
  if (!key.endsWith(`:${DEFAULT_SUFFIX}`)) return;
  const legacy = `${key.slice(0, -DEFAULT_SUFFIX.length)}${LEGACY_DEFAULT_SUFFIX}`;
  try {
    const store = storage();
    const old = store?.getItem(legacy);
    if (!store || old === null || old === undefined) return;
    if (store.getItem(key) === null) store.setItem(key, old);
    store.removeItem(legacy);
  } catch {
    // without storage there is nothing to move
  }
}

function trimToolCalls(calls: KeyedToolCall[]): KeyedToolCall[] {
  return calls.map((call) => ({
    ...call,
    ...(call.result && call.result.length > MAX_TOOL_RESULT ? { result: `${call.result.slice(0, MAX_TOOL_RESULT)}…` } : {}),
    ...(call.error && call.error.length > MAX_TOOL_RESULT ? { error: `${call.error.slice(0, MAX_TOOL_RESULT)}…` } : {}),
  }));
}

/** What is stored: the last 50 turns, tool calls only on the last 10, each tool result trimmed. */
export function compactTurns(turns: ChatTurn[]): ChatTurn[] {
  const kept = turns.slice(-MAX_STORED_TURNS);
  const firstWithCalls = kept.length - TOOL_CALL_TURNS;
  return kept.map((turn, index) => {
    if (!turn.toolCalls) return turn;
    if (index < firstWithCalls) return { ...turn, toolCalls: undefined }; // JSON leaves it out
    return { ...turn, toolCalls: trimToolCalls(turn.toolCalls) };
  });
}

/**
 * What is stored: compacted turns, at most 200 rows per widget, and about 2 MB in all. Past that the
 * oldest widgets lose their data (keeping spec and source, so Refresh reloads them), then the
 * oldest turns go.
 */
export function compactSession(session: ChatSession, maxChars = MAX_STORED_CHARS): ChatSession {
  let turns = compactTurns(session.turns);
  const widgets = session.widgets.map(capRows);
  const size = () => JSON.stringify({ turns, widgets, ...extras(session) }).length;
  for (let i = 0; i < widgets.length && size() > maxChars; i += 1) {
    if (widgets[i].block) widgets[i] = { ...widgets[i], block: null };
  }
  while (turns.length > 0 && size() > maxChars) turns = turns.slice(1);
  return { turns, widgets, ...extras(session) };
}

function read(key: string): StoredConversation | null {
  try {
    const raw = storage()?.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConversation>;
    if (typeof parsed?.updatedAt !== 'number' || !Array.isArray(parsed.turns)) return null;
    return {
      updatedAt: parsed.updatedAt,
      turns: parsed.turns,
      widgets: Array.isArray(parsed.widgets) ? parsed.widgets : [],
      ...extras(parsed),
    };
  } catch {
    return null;
  }
}

function remove(key: string): void {
  try {
    storage()?.removeItem(key);
  } catch {
    // nothing to do without storage
  }
}

/** A stored conversation. Expired ones are dropped; running turns come back interrupted. */
export function loadSession(key: string | null, now = Date.now()): ChatSession {
  if (!key) return EMPTY_SESSION;
  migrateLegacyDefault(key);
  const stored = read(key);
  if (!stored) return EMPTY_SESSION;
  if (now - stored.updatedAt > MAX_AGE_MS) {
    remove(key);
    return EMPTY_SESSION;
  }
  return {
    turns: stored.turns.map((turn) => (turn.status === 'running' ? { ...turn, status: 'interrupted' as const } : turn)),
    widgets: stored.widgets,
    ...extras(stored),
  };
}

export function saveSession(key: string | null, session: ChatSession, now = Date.now()): void {
  if (!key) return;
  if (session.turns.length === 0 && session.widgets.length === 0 && !session.dashboard) {
    remove(key);
    return;
  }
  try {
    storage()?.setItem(key, JSON.stringify({ updatedAt: now, ...compactSession(session) }));
  } catch {
    // quota or blocked storage: the conversation lives for this page only
  }
}

/** The stored turns alone (oldest first). */
export function loadConversation(key: string | null, now = Date.now()): ChatTurn[] {
  return loadSession(key, now).turns;
}

export function saveConversation(key: string | null, turns: ChatTurn[], now = Date.now()): void {
  saveSession(key, { turns, widgets: [] }, now);
}

export function clearConversation(key: string | null): void {
  if (key) remove(key);
}

function storedKeys(): string[] {
  try {
    const store = storage();
    if (!store) return [];
    const keys: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    return keys;
  } catch {
    return [];
  }
}

/** Drops every AI Insight conversation not updated for 7 days (other agents' too). */
export function sweepExpiredConversations(now = Date.now()): void {
  for (const key of storedKeys()) {
    const stored = read(key);
    if (!stored || now - stored.updatedAt > MAX_AGE_MS) remove(key);
  }
}

/** Sign-out: removes every AI Insight conversation kept in this browser. */
export function clearAiInsightConversations(): void {
  for (const key of storedKeys()) remove(key);
}
